import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import { Code, Copy, Check, ChevronDown } from 'lucide-react'
import { useEmbedCode } from '../../hooks/useEmbedCode'
import type { WidgetConfiguration } from '../../types'

interface EmbedCodeProps {
  config: WidgetConfiguration
  tenantSlug: string
  kbId: string
}

export function EmbedCode({ config, tenantSlug, kbId }: EmbedCodeProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [copiedSingle, setCopiedSingle] = useState(false)
  const [copiedLegacy, setCopiedLegacy] = useState(false)
  const { embedCode, embedCodeSingleScript } = useEmbedCode(config, tenantSlug, kbId)
  const { toast } = useToast()

  const handleCopySingle = async () => {
    try {
      await navigator.clipboard.writeText(embedCodeSingleScript)
      setCopiedSingle(true)
      toast({
        title: 'Copied!',
        description: 'Single-script embed code copied to clipboard',
      })
      setTimeout(() => setCopiedSingle(false), 2000)
    } catch (error) {
      toast({
        title: 'Failed to copy',
        description: 'Could not copy embed code to clipboard',
        variant: 'destructive',
      })
    }
  }

  const handleCopyLegacy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCopiedLegacy(true)
      toast({
        title: 'Copied!',
        description: 'Legacy embed code copied to clipboard',
      })
      setTimeout(() => setCopiedLegacy(false), 2000)
    } catch (error) {
      toast({
        title: 'Failed to copy',
        description: 'Could not copy embed code to clipboard',
        variant: 'destructive',
      })
    }
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="outline" className="w-full justify-between">
          <span className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            Embed Code
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4">
        <div className="space-y-4">
          {/* RECOMMENDED: Single Script */}
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-semibold">Single Script Embed</h4>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      Recommended
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    One line - settings auto-configured
                  </p>
                </div>
                <Button
                  onClick={handleCopySingle}
                  size="sm"
                  variant="ghost"
                >
                  {copiedSingle ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Code
                    </>
                  )}
                </Button>
              </div>

              <div className="relative">
                <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs">
                  <code className="text-foreground font-mono">
                    {embedCodeSingleScript}
                  </code>
                </pre>
              </div>

              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  <strong>✨ Benefits:</strong> Cleaner, simpler, auto-updates settings when you change them in the dashboard.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* LEGACY: Two Scripts */}
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-semibold">Two Script Embed</h4>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                      Legacy
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Settings + JS separated (for manual configuration)
                  </p>
                </div>
                <Button
                  onClick={handleCopyLegacy}
                  size="sm"
                  variant="ghost"
                >
                  {copiedLegacy ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Code
                    </>
                  )}
                </Button>
              </div>

              <div className="relative">
                <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs">
                  <code className="text-foreground font-mono">
                    {embedCode}
                  </code>
                </pre>
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <p className="text-xs text-blue-900">
                <strong>📍 Instructions:</strong> Add your chosen embed code before the closing <code className="bg-blue-100 px-1 rounded">&lt;/body&gt;</code> tag on any page where you want the chat widget to appear.
              </p>
            </CardContent>
          </Card>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
